import { Link, useSearch } from '@tanstack/react-router';
import { AlertCircle, Loader2, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { PublicPageShell } from '@/components/auth/PublicPageShell';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import AuthService from '@/services/AuthService';
import { AccountAccessError, validateEmail } from '../account-access.api';

type State =
  | { status: 'busy' }
  | { status: 'done' }
  | { status: 'failed'; message: string };

/**
 * Landing page of the email-verification mail (`/confirm-email?token=`).
 * Confirms on mount; the user only sees the outcome.
 */
export function ConfirmEmailPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [state, setState] = useState<State>(() =>
    token
      ? { status: 'busy' }
      : { status: 'failed', message: 'This confirmation link is incomplete.' },
  );
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    validateEmail(token)
      .then(async () => {
        // the cached session user still holds the old email / pending email_new
        const auth = AuthService.getInstance();
        if (auth.isAuthenticated()) await auth.refreshProfile();
        setState({ status: 'done' });
      })
      .catch((e) => {
        const message =
          e instanceof AccountAccessError && /expired/i.test(e.message)
            ? 'This confirmation link has expired. Request the change again from your settings.'
            : 'This confirmation link is invalid or has already been used.';
        setState({ status: 'failed', message });
      });
  }, [token]);

  const signedIn = AuthService.getInstance().isAuthenticated();
  const back = signedIn ? (
    <Button asChild tooltip={false} className="w-full">
      <Link to="/settings">Go to settings</Link>
    </Button>
  ) : (
    <Button asChild tooltip={false} className="w-full">
      <Link to="/login">Sign in</Link>
    </Button>
  );

  return (
    <PublicPageShell title="Confirm email address">
      {state.status === 'busy' && (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
          <Loader2 className="size-4 animate-spin" />
          Confirming your new email address…
        </div>
      )}
      {state.status === 'done' && (
        <div className="space-y-4">
          <Alert>
            <MailCheck className="h-4 w-4" />
            <AlertTitle>Email address confirmed</AlertTitle>
            <AlertDescription>
              Your new email address is now in use for notifications and account
              recovery.
            </AlertDescription>
          </Alert>
          {back}
        </div>
      )}
      {state.status === 'failed' && (
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{state.message}</AlertDescription>
          </Alert>
          {back}
        </div>
      )}
    </PublicPageShell>
  );
}
